import { LYRIC_LINE_LEAD_SECONDS } from 'fym-shared/src/music24/lyricTiming';
import type { MusicVideoLyricLine } from 'fym-shared/src/music24/musicVideoApi';
import {
  MUSIC_VIDEO_EFFECTS,
  MUSIC_VIDEO_EFFECT_DEFAULT_INTENSITY,
  type MusicVideoEffect,
} from 'fym-shared/src/music24/musicVideoEffects';
import { beatPulse, effectEnvelope, effectProgress } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { CAPTION_FALLBACK_SEC } from './previewClock';

/**
 * The pure maths of the effects in the live preview. The time functions are the shared ones of
 * `musicVideoEffectFrames.ts` (the render reads them too), so a piece shows at the same time with the
 * same strength. The looks are CSS sketches of the Remotion components
 * (`music24-api/src/video/compositions/aiReel/effects/`); the curves copy theirs where CSS can. Every
 * size is in `cqw` (a share of the frame width): the render sizes are px of a 1920 frame.
 */

/** px of the 1920 render frame to `cqw` of the preview. */
export function framePx(px: number): number {
  return (px / 1920) * 100;
}

export function numParam(effect: MusicVideoEffect, key: string, fallback: number): number {
  const value = effect.params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function textParam(effect: MusicVideoEffect, key: string, fallback: string): string {
  const value = effect.params[key];
  return typeof value === 'string' ? value : fallback;
}

export function intensityOf(effect: MusicVideoEffect): number {
  return Math.min(1, Math.max(0, numParam(effect, 'intensity', MUSIC_VIDEO_EFFECT_DEFAULT_INTENSITY)));
}

/** The CSS colour of the `colour` param: `accent` is the accent of the release theme. */
export function effectColour(effect: MusicVideoEffect, accent: string, fallback: string): string {
  const colour = textParam(effect, 'colour', fallback);
  if (colour === 'accent') {
    return accent;
  }
  return colour === 'black' ? '#0b0b0f' : '#ffffff';
}

/** A fixed noise in 0..1 for an integer (the hash of the render). */
export function hash01(value: number): number {
  const x = Math.sin(value * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/** 0 at the edges, 1 in the middle: the peak of a transition on its clip edge. */
export function bell(progress: number): number {
  return Math.sin(Math.PI * Math.min(1, Math.max(0, progress)));
}

export function inCubic(x: number): number {
  return x * x * x;
}

export function outCubic(x: number): number {
  return 1 - (1 - x) ** 3;
}

/** The back-out easing (a small overshoot), for a word that pops in. */
export function outBack(x: number, overshoot = 1.70158): number {
  const c3 = overshoot + 1;
  return 1 + c3 * (x - 1) ** 3 + overshoot * (x - 1) ** 2;
}

/** The flash of a flash cut: a fast rise to 40 % of the effect, then a slower fall. */
export function flashShape(progress: number): number {
  if (progress < 0.4) {
    return progress / 0.4;
  }
  return Math.max(0, 1 - (progress - 0.4) / 0.6);
}

/** The index of the last beat at or before `sec`; -1 before the first beat. */
export function beatIndexAt(sec: number, beats: readonly number[]): number {
  let index = -1;
  for (let at = 0; at < beats.length; at += 1) {
    if ((beats[at] ?? Infinity) > sec) {
      break;
    }
    index = at;
  }
  return index;
}

/** The move of the footage that the active effects give at `sec`. */
export interface PreviewMotion {
  /** `cqw`. */
  x: number;
  y: number;
  scale: number;
  rotateDeg: number;
  blurPx: number;
  brightness: number;
  saturate: number;
  /** The colour split in `cqw` (red one side, cyan the other). */
  split: number;
}

const NO_MOTION: PreviewMotion = {
  x: 0,
  y: 0,
  scale: 1,
  rotateDeg: 0,
  blurPx: 0,
  brightness: 1,
  saturate: 1,
  split: 0,
};

function motionOf(effect: MusicVideoEffect, sec: number, beats: readonly number[]): Partial<PreviewMotion> | null {
  const progress = effectProgress(effect, sec);
  if (progress === null) {
    return null;
  }
  const intensity = intensityOf(effect);
  const envelope = effectEnvelope(effect, sec);
  const peak = bell(progress);
  const first = progress < 0.5;
  const half = first ? progress * 2 : (progress - 0.5) * 2;
  const beat = beatIndexAt(sec, beats);
  switch (effect.kind) {
    case 'whip': {
      const direction = textParam(effect, 'direction', 'left');
      const horizontal = direction === 'left' || direction === 'right';
      const sign = direction === 'left' || direction === 'up' ? -1 : 1;
      const travel = first ? inCubic(half) : -(1 - outCubic(half));
      const move = sign * travel * framePx((horizontal ? 1920 : 1080) * 0.09 * (0.6 + 0.4 * intensity));
      return {
        x: horizontal ? move : 0,
        y: horizontal ? 0 : move,
        scale: 1 + 0.25 * peak,
        blurPx: peak * peak * (6 + 14 * intensity),
      };
    }
    case 'zoomThrough': {
      const reach = 0.5 + 0.9 * intensity;
      const inward = textParam(effect, 'direction', 'in') === 'in';
      const scale = first
        ? 1 + reach * (inward ? 1 : 0.3) * inCubic(half)
        : 1 + reach * (inward ? 0.5 : 1) * (1 - outCubic(half));
      return { scale, blurPx: peak * peak * (2 + 5 * intensity), brightness: 1 + 0.25 * peak * peak * intensity };
    }
    case 'flashCut':
      return { brightness: 1 + 0.6 * intensity * flashShape(progress) };
    case 'filmBurn':
      return { brightness: 1 + 0.25 * peak * intensity };
    case 'glitch': {
      const step = Math.floor(sec * 15);
      return {
        x: (hash01(step + 5) - 0.5) * framePx(30) * intensity * envelope,
        split: framePx(10 + 18 * intensity) * envelope,
      };
    }
    case 'beatShake': {
      const pulse = beatPulse(sec, beats, 0.22) * envelope;
      const angle = hash01(beat) * Math.PI * 2;
      const amplitude = framePx(28) * intensity * pulse;
      return {
        x: Math.cos(angle) * amplitude,
        y: Math.sin(angle) * amplitude * 0.7,
        rotateDeg: (hash01(beat + 17) - 0.5) * 2.4 * intensity * pulse,
        scale: 1 + 0.04 * intensity * envelope + 0.035 * intensity * pulse,
      };
    }
    case 'rgbSplit': {
      const pulse = beatPulse(sec, beats, 0.3);
      return {
        split: framePx(3 + 4 * intensity + 22 * intensity * pulse) * envelope,
        scale: 1 + 0.02 * intensity * pulse * envelope,
      };
    }
    case 'freezeStroke':
      return { scale: 1 + 0.05 * intensity * progress, saturate: 1 - 0.4 * envelope };
    case 'lyricKinetic':
    case 'lyricHook':
    case 'titleCard':
    case 'artistCard':
    case 'chapterCard':
    case 'splitScreen':
    case 'colourPulse':
    default:
      return null;
  }
}

/** The sum of the moves of every active effect at `sec`. Pure. */
export function previewFootageMotion(
  effects: readonly MusicVideoEffect[],
  sec: number,
  beats: readonly number[]
): PreviewMotion {
  return effects.reduce<PreviewMotion>((sum, effect) => {
    const motion = motionOf(effect, sec, beats);
    if (motion === null) {
      return sum;
    }
    return {
      x: sum.x + (motion.x ?? 0),
      y: sum.y + (motion.y ?? 0),
      scale: sum.scale * (motion.scale ?? 1),
      rotateDeg: sum.rotateDeg + (motion.rotateDeg ?? 0),
      blurPx: sum.blurPx + (motion.blurPx ?? 0),
      brightness: sum.brightness * (motion.brightness ?? 1),
      saturate: sum.saturate * (motion.saturate ?? 1),
      split: sum.split + (motion.split ?? 0),
    };
  }, NO_MOTION);
}

/** The CSS `transform` and `filter` of a move; null parts for no move. Pure. */
export function motionStyle(motion: PreviewMotion): { transform: string | undefined; filter: string } {
  const moves = motion.x !== 0 || motion.y !== 0 || motion.scale !== 1 || motion.rotateDeg !== 0;
  const filters: string[] = [];
  if (motion.blurPx > 0.05) {
    filters.push(`blur(${motion.blurPx.toFixed(2)}px)`);
  }
  if (motion.brightness !== 1) {
    filters.push(`brightness(${motion.brightness.toFixed(3)})`);
  }
  if (motion.saturate !== 1) {
    filters.push(`saturate(${motion.saturate.toFixed(3)})`);
  }
  if (motion.split > 0.01) {
    const split = motion.split.toFixed(2);
    filters.push(`drop-shadow(${split}cqw 0 rgb(255 30 60 / 55%)) drop-shadow(-${split}cqw 0 rgb(0 230 255 / 55%))`);
  }
  return {
    transform: moves
      ? `translate(${motion.x.toFixed(3)}cqw, ${motion.y.toFixed(3)}cqw) scale(${motion.scale.toFixed(4)}) rotate(${motion.rotateDeg.toFixed(3)}deg)`
      : undefined,
    filter: filters.join(' '),
  };
}

// ---------------------------------------------------------------------------------------------
// Lyric moments
// ---------------------------------------------------------------------------------------------

/** A lyric line in song seconds, with the display lead of every player. */
export interface PreviewLyricLine {
  index: number;
  text: string;
  startSec: number;
  endSec: number;
}

/** A word of a line with the time it is sung (the preview spreads the words evenly over the line). */
export interface PreviewLyricWord {
  text: string;
  startSec: number;
}

/** The timed lines in song seconds: the start with the lead, the end, else the next start, else the fallback. Pure. */
export function previewLyricLines(
  lines: readonly MusicVideoLyricLine[],
  leadSec = LYRIC_LINE_LEAD_SECONDS
): PreviewLyricLine[] {
  const timed = lines.filter((line) => line.startMs !== null);
  return timed.map((line, index) => {
    const startMs = line.startMs ?? 0;
    const next = timed[index + 1];
    const endMs = line.endMs ?? next?.startMs ?? startMs + CAPTION_FALLBACK_SEC * 1000;
    return { index: line.index, text: line.text, startSec: startMs / 1000 - leadSec, endSec: endMs / 1000 };
  });
}

export function isLyricEffect(effect: MusicVideoEffect): boolean {
  return MUSIC_VIDEO_EFFECTS[effect.kind].family === 'lyric';
}

/**
 * The line that a lyric effect shows at `sec`: the line that plays at `sec` inside the effect, else
 * the last line that started before `sec` and overlaps the effect. Null with no such line. Pure.
 */
export function effectLineAt(
  effect: MusicVideoEffect,
  lines: readonly PreviewLyricLine[],
  sec: number
): PreviewLyricLine | null {
  const overlapping = lines.filter((line) => line.startSec < effect.endSec && line.endSec > effect.startSec);
  const playing = overlapping.find((line) => sec >= line.startSec && sec < line.endSec);
  if (playing !== undefined) {
    return playing;
  }
  const started = overlapping.filter((line) => line.startSec <= sec);
  return started[started.length - 1] ?? overlapping[0] ?? null;
}

/** The words of a line, spread evenly over its sung time (the editor has no word times). Pure. */
export function lineWords(line: PreviewLyricLine): PreviewLyricWord[] {
  const words = line.text.split(/\s+/).filter((word) => word !== '');
  const lengthSec = Math.max(0.1, line.endSec - line.startSec);
  return words.map((text, index) => ({
    text,
    startSec: line.startSec + (index / Math.max(1, words.length)) * lengthSec,
  }));
}

/**
 * True when a caption line is hidden because a lyric effect shows it: the middle of its sung part
 * lies inside a lyric effect (the rule of the render, `withoutEffectLines`). Pure.
 */
export function isLineShownByEffect(line: PreviewLyricLine, effects: readonly MusicVideoEffect[]): boolean {
  const middleSec = (line.startSec + line.endSec) / 2;
  return effects.some((effect) => isLyricEffect(effect) && middleSec >= effect.startSec && middleSec < effect.endSec);
}
